import React, { useEffect, useState, useRef } from 'react';
import { cn } from '@/utils/classNames';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { NotificationState } from '@/store/slices/notificationsSlice';
import { useAppDispatch } from '@/store';
import { removeNotification } from '@/store/slices/notificationsSlice';
import { Button } from '../Button/Button';

interface ToastProps {
  notification: NotificationState;
  onClose: (id: string) => void;
}

export const Toast: React.FC<ToastProps> = ({ notification, onClose }) => {
  const dispatch = useAppDispatch();
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);
  const [isPaused, setIsPaused] = useState(false);

  // Refs to track time values between renders
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const pausedTimeRef = useRef<number>(0);
  const elapsedTimeRef = useRef<number>(0);

  // Handle auto-dismiss with progress bar
  useEffect(() => {
    if (!notification.autoDismiss) return;

    const animate = (timestamp: number) => {
      if (!startTimeRef.current) startTimeRef.current = timestamp;

      // Calculate elapsed time considering pauses
      const currentElapsed = timestamp - startTimeRef.current - pausedTimeRef.current;
      elapsedTimeRef.current = currentElapsed;

      // Calculate progress percentage (0-100)
      const newProgress = Math.max(0, 100 - (currentElapsed / notification.duration) * 100);

      setProgress(newProgress);

      if (newProgress > 0 && !isPaused) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    const setupTimer = () => {
      if (isPaused) return;

      // Clear existing timer if any
      if (timerRef.current) clearTimeout(timerRef.current);

      // Calculate remaining time based on current progress
      const remainingTime = (progress / 100) * notification.duration;

      timerRef.current = setTimeout(() => {
        handleClose();
      }, remainingTime);

      // Start or continue animation
      if (animationFrameRef.current === null) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    };

    // Start timer if not paused
    if (!isPaused) {
      setupTimer();
    } else if (animationFrameRef.current !== null) {
      // If paused, record the pause start time
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [notification.autoDismiss, notification.duration, notification.id, isPaused, progress]);

  // Update pause time when pausing/resuming
  useEffect(() => {
    if (isPaused) {
      // Store the time when paused
      pausedTimeRef.current = Date.now();
    } else if (pausedTimeRef.current > 0) {
      // Add the paused duration to the total paused time
      const pauseDuration = Date.now() - pausedTimeRef.current;
      pausedTimeRef.current = 0;
    }
  }, [isPaused]);

  const handleClose = () => {
    setIsExiting(true);
    // Wait for exit animation to complete
    setTimeout(() => {
      onClose(notification.id);
      dispatch(removeNotification(notification.id));
    }, 300);
  };

  const getIcon = () => {
    switch (notification.type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'info':
      default:
        return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getToastClasses = () => {
    const baseClasses = 'relative max-w-md rounded-lg shadow-lg border p-4 overflow-hidden';
    const typeClasses = {
      success: 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800',
      error: 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800',
      warning: 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800',
      info: 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800',
    };

    const animationClasses = isExiting ? 'animate-fade-out' : 'animate-slide-in';

    return cn(baseClasses, typeClasses[notification.type], animationClasses);
  };

  return (
    <div
      className={getToastClasses()}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Close button */}
      <button
        onClick={handleClose}
        className="absolute right-2 top-2 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        aria-label="Close notification"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Content */}
      <div className="flex gap-3">
        <div className="flex-shrink-0 mt-0.5">{getIcon()}</div>

        <div className="flex-1 pr-6">
          {notification.title && (
            <h3 className="font-medium text-gray-900 dark:text-gray-100">{notification.title}</h3>
          )}

          <div className="mt-1 text-sm text-gray-700 dark:text-gray-300">
            {notification.message}
          </div>

          {/* Action buttons */}
          {notification.actions && notification.actions.length > 0 && (
            <div className="mt-3 flex gap-2">
              {notification.actions.map((action, index) => (
                <Button
                  key={index}
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    action.onClick();
                    handleClose();
                  }}
                >
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Progress bar */}
      {notification.autoDismiss && (
        <div className="absolute bottom-0 left-0 h-1 bg-gray-200 dark:bg-gray-700 w-full">
          <div
            className={cn('h-full', {
              'bg-green-500': notification.type === 'success',
              'bg-red-500': notification.type === 'error',
              'bg-yellow-500': notification.type === 'warning',
              'bg-blue-500': notification.type === 'info',
              'transition-none': isPaused,
              'transition-all': !isPaused,
            })}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
};
